/**
 * Getting the app onto the home screen, which is the only place it is any good.
 *
 * Everything else in this directory keeps an installed app working; this is what
 * makes there be one. A breeder who opened the link in a browser has the whole
 * product — the worker, the caches, the write queue — behind an address bar, a
 * tab they will lose, and no icon to come back to. The offer has to be made in
 * the app itself, because the browser's own is a strip at the bottom of the
 * screen that is dismissed by accident and never shown again.
 *
 * There are two mechanisms and they share nothing:
 *
 *   `prompt`   Chromium (Android) fires `beforeinstallprompt` when the page is
 *              installable. The event is the permission to ask, and calling
 *              `prompt()` on it opens the system sheet.
 *   `manual`   iOS has no API at all. Safari's Share → Add to Home Screen is
 *              the only route, so the only thing the app can do is say where it
 *              is. Hence instructions rather than a button that installs.
 *
 * Phones only, deliberately. This is an app used one-handed beside a whelping
 * box; a desktop Chrome that would happily install it has no use for the icon,
 * and the offer would be noise on the one screen that is already about settings.
 *
 * The listeners are registered at module load and the state lives here rather
 * than in the hook, because `beforeinstallprompt` fires ONCE, seconds after the
 * page loads — long before anyone opens the settings screen that asks about it.
 * A listener added in an effect is a listener added after the event. That is why
 * `main.tsx` imports this module for its side effect.
 */
import { useSyncExternalStore } from 'react';

/**
 * Chromium's install event, which is in no lib: the spec is a draft and
 * TypeScript ships `WindowEventMap` without it. Declared rather than cast so the
 * handler below is checked against something.
 */
interface BeforeInstallPromptEvent extends Event {
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
  prompt: () => Promise<void>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

/** What, if anything, to offer: nothing, a button, or directions. */
export type InstallMode = 'none' | 'prompt' | 'manual';

/**
 * The widest phone, in CSS pixels of the *screen* rather than the viewport.
 *
 * The viewport is the wrong thing to measure: the app is portrait-locked in its
 * manifest but a browser tab is not, and a phone turned sideways is briefly as
 * wide as a tablet. `screen` does not move, and its short side separates the
 * largest phone (a Pro Max is 430) from the smallest tablet (an iPad mini is
 * 744) with room on both sides of the line.
 */
const PHONE_MAX = 540;

const isPhone = (): boolean =>
  window.matchMedia('(pointer: coarse)').matches &&
  Math.min(window.screen.width, window.screen.height) <= PHONE_MAX;

/**
 * An iPhone. iPads are excluded twice over — by `isPhone` and by this — and
 * iPadOS would not admit to being one here anyway: since iPadOS 13 it reports
 * itself as a Macintosh.
 */
const isIphone = (): boolean => /iPhone|iPod/.test(navigator.userAgent);

/**
 * Already on the home screen, in which case there is nothing to offer.
 *
 * Two checks because iOS never implemented `display-mode`: Safari answers this
 * question with a non-standard property on `navigator` and nothing else.
 */
const isInstalled = (): boolean =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

let deferred: BeforeInstallPromptEvent | null = null;

const compute = (): InstallMode => {
  if (isInstalled() || !isPhone()) return 'none';
  if (deferred !== null) return 'prompt';
  return isIphone() ? 'manual' : 'none';
};

/**
 * Cached, because `useSyncExternalStore` compares snapshots by identity and
 * re-renders forever if the getter answers with something new each time. It is
 * a string here, so that is free — but it also means the value only changes when
 * one of the two events below says so, which is the whole of what can change it.
 */
let snapshot: InstallMode = compute();

const listeners = new Set<() => void>();

const refresh = (): void => {
  const next = compute();
  if (next === snapshot) return;
  snapshot = next;
  for (const listener of listeners) listener();
};

window.addEventListener('beforeinstallprompt', (event) => {
  // Suppressing Chromium's own mini-infobar is the point of keeping the event:
  // the offer then appears where the user went looking for it, at a moment they
  // chose, instead of over the screen they are reading.
  event.preventDefault();
  deferred = event;
  refresh();
});

window.addEventListener('appinstalled', () => {
  deferred = null;
  refresh();
});

/**
 * Ask, on Android. Resolves when the user has answered the system sheet.
 *
 * The event is spent either way — Chromium refuses a second `prompt()` on the
 * same one — so it is dropped whichever way the answer went, and the offer
 * disappears with it. Chromium fires a fresh event on the next load if the
 * install was declined, which is what puts the offer back.
 */
export const promptInstall = async (): Promise<void> => {
  const event = deferred;
  if (event === null) return;

  try {
    await event.prompt();
    await event.userChoice;
  } finally {
    deferred = null;
    refresh();
  }
};

export const useInstallMode = (): InstallMode =>
  useSyncExternalStore(
    (onChange) => {
      listeners.add(onChange);
      return () => {
        listeners.delete(onChange);
      };
    },
    () => snapshot,
    // No server to render on, and nothing to offer if there ever is one.
    () => 'none',
  );
